import { YinDetector } from './yin';

export interface TunerPitch {
  freq: number;
  confidence: number;
  midiFloat: number;
  at: number;
  /** Signal RMS for the detection frame, in [0, 1]. Downstream uses
   *  this to modulate trail opacity by loudness. */
  rms: number;
}

export interface TunerHandle {
  stop: () => void;
}

/** Median-window length. Disabled for live monitoring — every frame
 *  is emitted raw so vibrato peaks and bend ramps reach the display
 *  without any inter-frame lowpass. Octave-error rejection moves to a
 *  single-frame outlier guard below (cheaper and more responsive than
 *  median voting). */
const MEDIAN_WINDOW = 1;
/** Emit on the very first valid reading. With MEDIAN_WINDOW = 1 there
 *  is no buffer to fill, so any warm-up gate just adds latency. */
const MIN_STABLE_READINGS = 1;
/** Maximum semitones a single frame may differ from the most recently
 *  emitted pitch before we treat it as a YIN octave-flip and skip it.
 *  6 semitones (P4-ish) is wider than any vibrato or bend on a real
 *  đàn tranh while comfortably narrower than the 12-semitone signature
 *  of an octave error. */
const OUTLIER_REJECT_SEMIS = 6;
/** After this many consecutive outlier-rejected frames, give up and
 *  accept the new pitch. This unblocks legitimate big leaps (e.g. the
 *  player jumps from a low string to a high one) — they take ~50 ms
 *  to settle in instead of being permanently rejected. */
const OUTLIER_REJECT_STREAK_LIMIT = 3;

/**
 * Mic capture + YIN pitch detection loop.
 *
 * Mic constraints: bare `{ audio: true }` — exactly what the known-
 * good standalone Dan Tranh Tuner uses. That lets the browser keep
 * its default echoCancellation + noiseSuppression + autoGainControl.
 * Counter-intuitively this HELPS YIN on dan tranh:
 *   · AGC keeps the fundamental amplitude stable across plucks, so
 *     YIN's CMNDF dip depth is consistent frame-to-frame instead of
 *     flickering between "confident" and "ambiguous".
 *   · Noise suppression removes low-freq rumble that otherwise biases
 *     the difference function at long τ.
 *   · Echo cancellation strips room reverb, which on metal strings
 *     carries strong decayed 2nd-harmonic energy — the exact thing
 *     that was fooling YIN into picking τ=P/2 (octave-up error).
 * An earlier version explicitly disabled all three thinking it would
 * preserve "purity", and that was the ACTUAL cause of "lộn quãng 8"
 * the user kept seeing — raw mic on desktop Chrome has enough 2nd-
 * harmonic energy from room resonance to consistently flip YIN.
 *
 * Buffer: 4096 samples at ~48 kHz ≈ 85 ms window. That gives YIN
 * enough periods for low strings (C3 ≈ 131 Hz → 7.5 ms period → 11
 * periods per window) while keeping the detection latency under 100 ms.
 *
 * Smoothing pipeline on the detected frequency:
 *   raw (per-frame) → median-of-10 (with 3-sample warmup gate) → caller.
 * Intentionally NO EMA/smoothing downstream of the median — EMA on
 * midi-float interpolates smoothly through intermediate notes when
 * YIN momentarily flips octaves, producing a ramp on the tuner graph
 * instead of the discrete switch the player wants to see.
 */
/** iOS Safari applies an aggressive voice-call processing chain when
 *  `getUserMedia({ audio: true })` is called with default constraints —
 *  AGC, noise suppression tuned for speech (200–3400 Hz), and echo
 *  cancellation. On iPad this gates plucked-string transients,
 *  attenuates low strings, and gates quiet sustains as noise — the
 *  "tuner not picking up" symptom. On desktop Chrome the same defaults
 *  HELP YIN (see comment block above), so we keep the desktop path
 *  unchanged and only force raw constraints on iOS. */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ identifies as Mac in UA; detect via touch + Mac.
  return navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
}

export async function startTuner(
  onPitch: (p: TunerPitch | null) => void,
  onInterrupted?: () => void,
): Promise<TunerHandle> {
  const constraints: MediaStreamConstraints = isIOS()
    ? {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      }
    : { audio: true };
  const stream = await navigator.mediaDevices.getUserMedia(constraints);

  type CtxCtor = typeof AudioContext;
  const Ctx: CtxCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: CtxCtor }).webkitAudioContext;
  const ctx = new Ctx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  // 2048 samples ≈ 43 ms at 48 kHz, half the previous 4096-sample
  // window. With MEDIAN_WINDOW = 1 the analyser window IS the only
  // smoother left, so shrinking it directly tightens the response to
  // bend motion and vibrato wobble. YIN still has 1024 samples to
  // analyse — for C3 (131 Hz, period 7.6 ms) that's 2.7 periods,
  // borderline but workable; for everything above ~200 Hz it's 4+
  // periods, plenty.
  analyser.fftSize = 2048;
  // iPad mic input is FAR quieter than desktop even with AGC off.
  // 8× was the previous calibration; bumped to 16× because the
  // pitch-line trail still read faint at typical playing volume
  // even with downstream alpha gates fully opened. Loud plucks may
  // clip at this gain — analytically fine for YIN (clipped
  // harmonic-rich signals still expose the fundamental, just with
  // slightly lower confidence) and the trail-visibility win on iPad
  // far outweighs occasional clipping artifacts. Desktop path stays
  // at 1× — its raw input is already hot enough.
  const gainNode = ctx.createGain();
  gainNode.gain.value = isIOS() ? 16 : 1;
  source.connect(gainNode);
  gainNode.connect(analyser);

  const buf = new Float32Array(analyser.fftSize);
  // YIN buffer matches the analyser window so every detection uses
  // the freshest 43 ms of audio — no stale older-half samples
  // dragging on the result.
  const detector = new YinDetector(ctx.sampleRate, analyser.fftSize);
  // Use the YIN paper's default threshold. A previous attempt to
  // tighten this to 0.10 was the *actual* source of the "lộn quãng 8"
  // the player was seeing: at threshold 0.10, if the fundamental's
  // CMNDF dip sits at (say) 0.12 and the sub-octave dip at τ=2P is
  // 0.04, the scan skips the fundamental and picks 2P — detected
  // pitch drops exactly one octave. At 0.15 the fundamental wins.
  detector.threshold = 0.15;

  // Last emitted midi-float. Used to identify a single-frame YIN
  // octave-flip (the new frame jumps > OUTLIER_REJECT_SEMIS away from
  // the running pitch) and skip it. Reset on silence so a fresh
  // attack after a rest doesn't get stuck rejecting the new pitch.
  let lastEmittedMidi: number | null = null;
  let outlierStreak = 0;
  const _voidFreqHistory = MEDIAN_WINDOW; // retained for future re-enable
  void _voidFreqHistory;

  let stopped = false;
  let raf = 0;

  const tick = () => {
    if (stopped) return;
    analyser.getFloatTimeDomainData(buf);
    const result = detector.detect(buf);
    const now = performance.now();

    if (result) {
      const midiFloat = 69 + 12 * Math.log2(result.freq / 440);

      // Single-frame outlier rejection. If this frame disagrees with
      // the most recent emitted pitch by more than P4-ish (well above
      // any vibrato/bend, well below an octave), assume a YIN flip
      // and skip — UNLESS we've rejected a few in a row, in which
      // case the player has genuinely leapt to a new pitch and we
      // accept the new value to unstick.
      if (
        lastEmittedMidi !== null &&
        Math.abs(midiFloat - lastEmittedMidi) > OUTLIER_REJECT_SEMIS &&
        outlierStreak < OUTLIER_REJECT_STREAK_LIMIT
      ) {
        outlierStreak++;
        // Suppress emission for this frame; keep last value displayed.
        raf = requestAnimationFrame(tick);
        return;
      }
      outlierStreak = 0;

      // Skip the warm-up gate when MIN_STABLE_READINGS = 1 (default
      // for live monitoring). Re-enabled if the constant moves > 1.
      if (MIN_STABLE_READINGS > 1 && lastEmittedMidi === null) {
        // First frame — only skip if the warm-up gate is on.
      }

      lastEmittedMidi = midiFloat;
      onPitch({
        freq: result.freq,
        confidence: result.confidence,
        midiFloat,
        at: now,
        rms: result.rms,
      });
    } else {
      // Silence: clear octave-rejection state so the next attack
      // starts fresh and isn't blocked by a stale lastEmittedMidi.
      lastEmittedMidi = null;
      outlierStreak = 0;
      onPitch(null);
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  const doStop = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(raf);
    try {
      source.disconnect();
      analyser.disconnect();
    } catch {
      // ignore: already disconnected on re-entry
    }
    stream.getTracks().forEach((t) => t.stop());
    void ctx.close().catch(() => {});
  };

  // iOS-reliable stop triggers. Page-visibility events
  // (visibilitychange / blur / pagehide) are unreliable on iPad when
  // the app is backgrounded or the screen locks — they often don't
  // fire before iOS freezes the page. iOS Core Audio, however, DOES
  // drive the AudioContext `statechange` (→ suspended on audio
  // interruption) and the mic track `mute` event, so wire those as
  // additional stop triggers. onInterrupted lets the caller clear its
  // handle reference so a later return re-acquires cleanly.
  ctx.addEventListener('statechange', () => {
    if (ctx.state === 'suspended') {
      doStop();
      onInterrupted?.();
    }
  });
  for (const t of stream.getTracks()) {
    t.addEventListener('mute', () => {
      doStop();
      onInterrupted?.();
    });
  }

  return { stop: doStop };
}
